/**
 * HISIP-014: Billing Tab Component
 * 
 * Billing status, credit balance, and auto-refill settings
 * Integrates with existing billing API endpoints
 */

import React, { useState } from 'react';
import { CreditCard, DollarSign, Settings, ExternalLink, AlertCircle, CheckCircle, Zap } from 'lucide-react';
import { useServerBilling, AutoRefillSettings } from '@/hooks/useBilling-server';
import { cn } from '@/utils/cn';

const BillingTab: React.FC = () => {
  const { 
    status, 
    balance, 
    loading, 
    error, 
    refetch,
    updateAutoRefill,
    createCheckoutSession 
  } = useServerBilling();

  const [autoRefillSettings, setAutoRefillSettings] = useState<AutoRefillSettings>({
    enabled: false,
    minThreshold: 5,
    amount: 20
  });
  const [hasAutoRefillChanges, setHasAutoRefillChanges] = useState(false);
  const [isSavingAutoRefill, setIsSavingAutoRefill] = useState(false);

  const handleAutoRefillChange = (field: keyof AutoRefillSettings, value: boolean | number) => {
    setAutoRefillSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasAutoRefillChanges(true);
  };

  const handleSaveAutoRefill = async () => {
    setIsSavingAutoRefill(true);
    const success = await updateAutoRefill(autoRefillSettings);
    if (success) {
      setHasAutoRefillChanges(false);
    }
    setIsSavingAutoRefill(false);
  };

  const handleSubscribe = () => {
    createCheckoutSession('subscription');
  };

  const handleTopUp = (amount: 10 | 25 | 100) => {
    createCheckoutSession('topup', amount);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner w-8 h-8 border-cyan-400"></div>
        <span className="ml-3 text-cyan-400 font-mono">LOADING BILLING...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 font-mono mb-4">ERROR: {error}</div>
        <button 
          onClick={refetch}
          className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10"
        >
          RETRY
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <CreditCard className="w-5 h-5 text-cyan-400" />
        <div>
          <h3 className="text-lg font-mono text-cyan-400">BILLING & CREDITS</h3>
          <p className="text-white/50 text-sm font-mono">
            Manage subscription, credits, and auto-refill settings
          </p>
        </div>
      </div>

      {/* Admin Badge */}
      {status?.isAdmin && (
        <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-purple-400" />
            <div>
              <h4 className="text-purple-400 font-mono text-sm">BILLING BYPASS ACTIVE</h4>
              <p className="text-white/70 text-xs font-mono">
                Administrative account - billing restrictions disabled for development and administration
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Status */}
      <div className="bg-black/30 border border-cyan-500/20 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-cyan-400 font-mono text-sm">SUBSCRIPTION STATUS</h4>
          {status?.hasActiveSubscription ? (
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs font-mono">ACTIVE</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs font-mono">INACTIVE</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-mono text-white/50 mb-2">STATUS</label>
            <div className="p-3 bg-black/50 border border-cyan-500/30 rounded">
              <span className={cn(
                "font-mono text-sm",
                status?.hasActiveSubscription ? "text-green-400" : "text-red-400"
              )}>
                {status?.hasActiveSubscription ? "SUBSCRIBED" : "NOT SUBSCRIBED"}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono text-white/50 mb-2">BRAND</label>
            <div className="p-3 bg-black/50 border border-cyan-500/30 rounded">
              <span className="font-mono text-sm text-cyan-400 uppercase">
                {status?.brand || 'hi-si'}
              </span>
            </div>
          </div>
        </div>

        {!status?.hasActiveSubscription && !status?.isAdmin && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <button
              onClick={handleSubscribe}
              className="btn-future text-green-400 border-green-400/50 hover:bg-green-400/10 w-full"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              SUBSCRIBE NOW
            </button>
          </div>
        )}
      </div>

      {/* Credit Balance */}
      <div className="bg-black/30 border border-cyan-500/20 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-cyan-400 font-mono text-sm">CREDIT BALANCE</h4>
          <button
            onClick={refetch}
            className="text-white/40 hover:text-cyan-400 transition-colors"
            title="Refresh balance"
          >
            <Zap className="w-4 h-4" />
          </button>
        </div>

        <div className="mb-4">
          <div className="text-3xl font-mono text-green-400 mb-2">
            {status?.isAdmin ? (
              <span className="text-purple-400">BYPASS ACTIVE</span>
            ) : (
              balance ? formatCurrency(balance.balance) : '--'
            )}
          </div>
          <div className="text-xs text-white/50 font-mono">
            {status?.isAdmin ? (
              'Administrative billing bypass - no credit limits'
            ) : (
              'Available credits for pipeline executions'
            )}
          </div>
        </div>

        {!status?.isAdmin ? (
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => handleTopUp(10)}
              className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10 text-sm"
            >
              +$10
            </button>
            <button
              onClick={() => handleTopUp(25)}
              className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10 text-sm"
            >
              +$25
            </button>
            <button
              onClick={() => handleTopUp(100)}
              className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10 text-sm"
            >
              +$100
            </button>
          </div>
        ) : (
          <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded text-center">
            <span className="text-purple-400 font-mono text-sm">BILLING OPERATIONS BYPASSED</span>
          </div>
        )}
      </div>

      {/* Auto-Refill Settings */}
      {!status?.isAdmin ? (
        <div className="bg-black/30 border border-cyan-500/20 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-cyan-400 font-mono text-sm">AUTO-REFILL SETTINGS</h4>
            <div className="flex items-center gap-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefillSettings.enabled}
                  onChange={(e) => handleAutoRefillChange('enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer 
                               peer-checked:after:translate-x-full peer-checked:after:border-white 
                               after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                               after:bg-white after:border-gray-300 after:border after:rounded-full 
                               after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
              </label>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono text-white/50 mb-2">
                  TRIGGER THRESHOLD
                </label>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-white/40" />
                  <input
                    type="number"
                    min="1"
                    max="50"
                    step="1"
                    value={autoRefillSettings.minThreshold || 5}
                    onChange={(e) => handleAutoRefillChange('minThreshold', Number(e.target.value))}
                    disabled={!autoRefillSettings.enabled}
                    className="flex-1 bg-black/50 border border-cyan-500/30 rounded p-2
                             text-white/90 font-mono text-sm
                             focus:outline-none focus:border-cyan-500/50
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <p className="text-xs text-white/40 mt-1">
                  Auto-refill when balance falls below this amount
                </p>
              </div>

              <div>
                <label className="block text-xs font-mono text-white/50 mb-2">
                  REFILL AMOUNT
                </label>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-white/40" />
                  <input
                    type="number"
                    min="10"
                    max="100"
                    step="5"
                    value={autoRefillSettings.amount || 20}
                    onChange={(e) => handleAutoRefillChange('amount', Number(e.target.value))}
                    disabled={!autoRefillSettings.enabled}
                    className="flex-1 bg-black/50 border border-cyan-500/30 rounded p-2
                             text-white/90 font-mono text-sm
                             focus:outline-none focus:border-cyan-500/50
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <p className="text-xs text-white/40 mt-1">
                  Amount to add when auto-refill triggers
                </p>
              </div>
            </div>

            {hasAutoRefillChanges && (
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSaveAutoRefill}
                  disabled={isSavingAutoRefill}
                  className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10 flex items-center gap-2"
                >
                  {isSavingAutoRefill ? (
                    <>
                      <div className="w-4 h-4 border border-cyan-400 border-t-transparent rounded-full animate-spin" />
                      <span>SAVING...</span>
                    </>
                  ) : (
                    <>
                      <Settings className="w-4 h-4" />
                      <span>SAVE SETTINGS</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-black/30 border border-purple-500/20 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-4">
            <Settings className="w-5 h-5 text-purple-400" />
            <h4 className="text-purple-400 font-mono text-sm">AUTO-REFILL SETTINGS</h4>
          </div>
          <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded text-center">
            <span className="text-purple-400 font-mono text-sm">DISABLED FOR ADMINISTRATIVE ACCOUNTS</span>
          </div>
        </div>
      )}

      {/* Management Portal Link */}
      {status?.hasActiveSubscription && !status?.isAdmin && (
        <div className="bg-black/20 border border-white/10 rounded-lg p-4">
          <h4 className="text-white/70 font-mono text-sm mb-3">BILLING MANAGEMENT</h4>
          <p className="text-white/50 text-xs font-mono mb-3">
            Manage your subscription, payment methods, and billing history through Stripe's customer portal.
          </p>
          <button
            onClick={() => {
              // This would integrate with the portal session creation
              console.log('Opening billing portal...');
            }}
            className="btn-future text-white/70 border-white/30 hover:bg-white/5 flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            OPEN BILLING PORTAL
          </button>
        </div>
      )}

      {/* Info */}
      <div className="text-xs text-white/50 font-mono p-4 bg-black/20 border border-white/10 rounded-lg">
        <p className="mb-2">
          • Credits are consumed based on AI provider usage during pipeline execution
        </p>
        <p className="mb-2">
          • Auto-refill helps ensure your pipelines never stop due to insufficient credits
        </p>
        <p>
          • All payments are securely processed through Stripe
        </p>
      </div>
    </div>
  );
};

export default BillingTab;